import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ChevronLeft, ChevronRight, Loader2, Filter, Copy, AlertCircle, AlertTriangle,
  CheckCircle2, Building2, Target, X,
} from 'lucide-react';
import { WeekPicker } from './WeekPicker';
import { FilterPill } from './FilterPill';
import { FilterPopover } from './FilterPopover';
import { FirmCountChips } from './FirmCountChips';
import { PlanningActionsMenu } from './PlanningActionsMenu';

/**
 * The full primary toolbar of the planning week board. Houses week
 * navigation, copy-from-previous, Depo & Rota dialog opener, the İşlemler
 * dropdown, the multi-select Board firm strip, the Filters popover trigger,
 * and the stats badges (assignments / missing cells / conflicts) plus the
 * per-firm count chips on the right.
 *
 * State in this component is intentionally local to ephemeral UI affordances
 * only (filters popover open/close); everything else is lifted to Planning.jsx
 * since the board grid and assignment dialogs read the same state.
 */
export function PlanningToolbar({
  // week nav
  year, week, board, t,
  goPrevWeek, goNextWeek, setYear, setWeek,
  onWeekCopyDone,
  // role-gated actions
  isSuperAdmin,
  canCopyWeek,
  onCopyFromPrev,
  onOpenDepotManager,
  onOpenImportExport,
  onClearWeek,
  // weekend toggles
  showSunday, setShowSunday,
  // board firm filter (multi-select Set)
  filterFirmIds, setFilterFirmIds,
  // filters popover passthrough
  filtersOpen, setFiltersOpen,
  filterCompanyIds, setFilterCompanyIds,
  filterDepotIds, setFilterDepotIds,
  filterRouteIds, setFilterRouteIds,
  empFilter, setEmpFilter,
  clearAllFilters,
  activeFilterCount,
  // stats
  loading,
  assignedCount, sundayAssignedCount,
  missingCount,
  conflictCount, onOpenConflicts,
  // firm count chips
  firmAssignmentCounts, openFirmPopover, setOpenFirmPopover,
  dates, isDateLocked, lockedDates,
  // focus mode (employee double-click)
  focusedEmp,
  focusedEmpAssignedCount,
  onClearFocus,
}) {
  return (
    <Card className="px-3 py-2 flex flex-wrap items-center gap-2">
      {/* Şub 2026 — "Önceki Haftadan Kopyala" butonu en solda: en sık kullanılan
          hızlı aksiyon. Aktif filtreler varsa vurgulanır (mavi border) —
          operatör sadece filtre kapsamındaki atamaların kopyalanacağını görsün. */}
      {(isSuperAdmin || canCopyWeek) && (
        <Button
          variant="outline"
          size="sm"
          className={`h-8 ${activeFilterCount > 0 ? 'border-blue-400 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20' : ''}`}
          onClick={onCopyFromPrev}
          data-testid="planning-copy-week"
          title={activeFilterCount > 0 ? `Önceki haftadan kopyala (${activeFilterCount} aktif filtre uygulanacak)` : 'Önceki haftadan kopyala'}
        >
          <Copy className="h-3.5 w-3.5 mr-1" /> {t('planning.copyFromPrevWeek')}
          {activeFilterCount > 0 && (
            <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full bg-blue-500 text-white font-bold" data-testid="planning-copy-week-filter-badge">
              {activeFilterCount}
            </span>
          )}
        </Button>
      )}
      <div className="flex items-center gap-1">
        <Button variant="outline" size="icon" onClick={goPrevWeek} title="Önceki hafta" data-testid="planning-prev-week">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <WeekPicker
          year={year} week={week}
          onPick={(y, w) => { setYear(y); setWeek(w); }}
          onCopyDone={onWeekCopyDone}
        />
        <Button variant="outline" size="icon" onClick={goNextWeek} title={t('planning.nextWeek')} data-testid="planning-next-week">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <div className="text-xs text-slate-500">
        {board?.week?.start_date} → {board?.week?.end_date}
      </div>
      {isSuperAdmin && (
        <Button variant="outline" size="sm" className="h-8" onClick={onOpenDepotManager} data-testid="planning-depot-manager-open">
          <Building2 className="h-3.5 w-3.5 mr-1" /> {t('planning.depotAndRoutes')}
        </Button>
      )}
      <PlanningActionsMenu
        showSunday={showSunday}
        setShowSunday={setShowSunday}
        isSuperAdmin={isSuperAdmin}
        canCopyWeek={canCopyWeek}
        onOpenImportExport={onOpenImportExport}
        onClearWeek={onClearWeek}
        t={t}
      />
      {/* Board firm strip — multi-select; INDEPENDENT from left-panel
          employee firm chip in PlanningLeftPanel. */}
      <div className="flex items-center gap-1 pl-1 border-l border-slate-200 dark:border-slate-700 ml-1" data-testid="planning-board-firm-strip">
        <span className="text-[10px] uppercase tracking-wide text-slate-400 mr-1">Board</span>
        <FilterPill
          active={filterFirmIds.size === 0}
          onClick={() => setFilterFirmIds(new Set())}
          testId="planning-board-firm-all"
        >
          Tümü
        </FilterPill>
        {(board?.firms || []).map((f) => (
          <FilterPill
            key={f.id}
            active={filterFirmIds.has(f.id)}
            onClick={() => setFilterFirmIds((prev) => {
              const next = new Set(prev);
              if (next.has(f.id)) next.delete(f.id);
              else next.add(f.id);
              return next;
            })}
            color="indigo"
            testId={`planning-board-firm-${f.id}`}
          >
            {f.name}
          </FilterPill>
        ))}
      </div>
      <div className="relative">
        <Button
          variant="outline"
          size="sm"
          className="h-8"
          onClick={() => setFiltersOpen((v) => !v)}
          data-testid="planning-filters-toggle"
        >
          <Filter className="h-3.5 w-3.5 mr-1" />
          Filtreler
          {activeFilterCount > 0 && (
            <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full bg-emerald-500 text-white font-bold">
              {activeFilterCount}
            </span>
          )}
        </Button>
        {filtersOpen && (
          <FilterPopover
            board={board}
            filterCompanyIds={filterCompanyIds}
            setFilterCompanyIds={setFilterCompanyIds}
            filterDepotIds={filterDepotIds}
            setFilterDepotIds={setFilterDepotIds}
            filterRouteIds={filterRouteIds}
            setFilterRouteIds={setFilterRouteIds}
            empFilter={empFilter}
            setEmpFilter={setEmpFilter}
            clearAll={clearAllFilters}
            close={() => setFiltersOpen(false)}
          />
        )}
      </div>
      <div className="ml-auto flex items-center gap-2 flex-wrap justify-end">
        {focusedEmp && (
          <span
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-rose-50 dark:bg-rose-900/30 border border-rose-300 text-rose-700 dark:text-rose-300 text-xs font-semibold"
            data-testid="planning-focus-badge"
            title="ESC tuşu ile veya çarpıya basarak odak modundan çık"
          >
            <Target className="h-3 w-3" />
            <span className="truncate max-w-[160px]">Odak: {focusedEmp.full_name}</span>
            <span className="font-mono text-[10px] opacity-80">· {focusedEmpAssignedCount}/7</span>
            <button
              type="button"
              onClick={onClearFocus}
              className="ml-0.5 -mr-1 p-0.5 rounded-full hover:bg-rose-200 dark:hover:bg-rose-800/50"
              title="Odak modunu kapat (ESC)"
              data-testid="planning-focus-clear"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        )}
        <FirmCountChips
          counts={firmAssignmentCounts}
          openFirmId={openFirmPopover}
          setOpenFirmId={setOpenFirmPopover}
          dates={dates}
          isDateLocked={isDateLocked}
          lockedDates={lockedDates}
        />
        {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
        <Badge variant="outline" className="text-xs" data-testid="planning-stats-assigned">
          <CheckCircle2 className="h-3 w-3 mr-0.5 text-emerald-500" />
          {assignedCount} atama
          {sundayAssignedCount > 0 && (
            <span className="ml-1 text-[10px] text-amber-600 dark:text-amber-400" title="Pazar günü ek atamalar">
              +{sundayAssignedCount} Pazar
            </span>
          )}
        </Badge>
        <Badge variant="outline" className={`text-xs ${missingCount > 0 ? 'border-amber-300 text-amber-700' : ''}`} data-testid="planning-stats-missing" title="Pzt-Cmt hafta içi boş hücreler (Pazar hariç)">
          <AlertCircle className="h-3 w-3 mr-0.5" />
          {missingCount} boş hücre
        </Badge>
        {conflictCount > 0 && (
          <button
            onClick={onOpenConflicts}
            className="text-xs px-2.5 py-0.5 rounded-md border border-red-300 text-red-700 hover:bg-red-50 transition flex items-center gap-1 font-semibold"
            data-testid="planning-stats-conflict"
            title={t('planning.viewConflicts')}
          >
            <AlertTriangle className="h-3 w-3" />
            {conflictCount} çakışma
          </button>
        )}
      </div>
    </Card>
  );
}
